import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Trophy, User, ChevronDown, Radio, Play, Calendar, Clock, MapPin } from "lucide-react";
import axios from "axios";
import { NotificationBell } from "./NotificationSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ASN Logo - Use the local PWA icon for consistency
const ASN_LOGO = "/asn-logo-main.png";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [publishedEvents, setPublishedEvents] = useState([]);
  const [featuredLeagues, setFeaturedLeagues] = useState([]);
  const [channels, setChannels] = useState([]);
  const [showLeaguesDropdown, setShowLeaguesDropdown] = useState(false);
  const [showChannelsDropdown, setShowChannelsDropdown] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [myTeamsSchedule, setMyTeamsSchedule] = useState([]);
  const [myTeams, setMyTeams] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const userMenuRef = useRef(null);
  const leaguesDropdownRef = useRef(null);
  const channelsDropdownRef = useRef(null);
  const channelsHoverTimeout = useRef(null);
  const navigate = useNavigate();

  // Refresh user data from server to ensure profile picture is current
  const refreshUserData = async () => {
    const token = localStorage.getItem("userToken");
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        // Update localStorage and state with fresh user data
        localStorage.setItem("userData", JSON.stringify(response.data));
        setCurrentUser(response.data);
      }
    } catch (err) {
      // Token might be invalid - clear user data
      if (err.response?.status === 401) {
        localStorage.removeItem("userToken");
        localStorage.removeItem("userData");
        setCurrentUser(null);
      }
    }
  };

  // Check for logged in user and refresh data
  useEffect(() => {
    const checkUser = () => {
      const userData = localStorage.getItem("userData");
      if (userData) {
        try {
          setCurrentUser(JSON.parse(userData));
        } catch (e) {
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
    };
    
    checkUser();
    
    // Refresh user data from server to ensure profile picture is current
    // Using AbortController for cleanup
    const controller = new AbortController();
    const token = localStorage.getItem("userToken");
    if (token) {
      axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      }).then(response => {
        if (response.data) {
          localStorage.setItem("userData", JSON.stringify(response.data));
          setCurrentUser(response.data);
        }
      }).catch(err => {
        if (err.name !== 'AbortError' && err.response?.status === 401) {
          localStorage.removeItem("userToken");
          localStorage.removeItem("userData");
          setCurrentUser(null);
        }
      });
    }
    
    window.addEventListener("storage", checkUser);
    return () => {
      window.removeEventListener("storage", checkUser);
      controller.abort();
    };
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (leaguesDropdownRef.current && !leaguesDropdownRef.current.contains(event.target)) {
        setShowLeaguesDropdown(false);
      }
      if (channelsDropdownRef.current && !channelsDropdownRef.current.contains(event.target)) {
        setShowChannelsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userData");
    setCurrentUser(null);
    setShowUserMenu(false);
    navigate("/");
    window.dispatchEvent(new Event("storage"));
  };

  // Fetch followed teams schedule when user menu opens
  useEffect(() => {
    if (!showUserMenu || !currentUser) return;
    const token = localStorage.getItem("userToken");
    if (!token) return;
    
    setScheduleLoading(true);
    axios.get(`${API}/users/preferences/followed-teams/schedule?limit=10`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setMyTeamsSchedule(res.data.games || []);
      setMyTeams(res.data.teams || []);
    }).catch(() => {
      setMyTeamsSchedule([]);
      setMyTeams([]);
    }).finally(() => setScheduleLoading(false));
  }, [showUserMenu, currentUser]);

  // Fetch menu items
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        // Add timestamp to prevent caching
        const response = await axios.get(`${API}/settings/menu`, {
          params: { _t: Date.now() },
          headers: { 'Cache-Control': 'no-cache' }
        });
        // Only show visible items, limit to 8 max
        const visibleItems = (response.data || [])
          .filter(item => item.is_visible !== false)
          .slice(0, 10);
        setMenuItems(visibleItems);
      } catch (error) {
        // Default menu items
        setMenuItems([
          { name: "Schedule", href: "/schedule" },
          { name: "On Demand", href: "/on-demand" },
          { name: "News", href: "/news" },
          { name: "Podcasts", href: "/podcasts" },
        ]);
      }
    };
    fetchMenuItems();
    
    // Also listen for menu updates from admin
    const handleMenuUpdate = () => fetchMenuItems();
    window.addEventListener("menuUpdated", handleMenuUpdate);
    return () => window.removeEventListener("menuUpdated", handleMenuUpdate);
  }, []);

  // Fetch published events
  useEffect(() => {
    const fetchPublishedEvents = async () => {
      try {
        const response = await axios.get(`${API}/events?include_unpublished=false`);
        setPublishedEvents(response.data || []);
      } catch (error) {
        setPublishedEvents([]);
      }
    };
    fetchPublishedEvents();
  }, []);

  // Fetch featured leagues for menu
  useEffect(() => {
    const fetchFeaturedLeagues = async () => {
      try {
        const response = await axios.get(`${API}/leagues/menu`);
        setFeaturedLeagues(response.data || []);
      } catch (error) {
        setFeaturedLeagues([]);
      }
    };
    fetchFeaturedLeagues();
  }, []);

  // Fetch channels for dropdown
  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const response = await axios.get(`${API}/channels`);
        // Filter to only active/enabled channels with logos
        const activeChannels = (response.data || []).filter(ch => ch.is_enabled !== false);
        setChannels(activeChannels);
      } catch (error) {
        setChannels([]);
      }
    };
    fetchChannels();
  }, []);

  // Handle channels dropdown hover with delay
  const handleChannelsMouseEnter = () => {
    if (channelsHoverTimeout.current) {
      clearTimeout(channelsHoverTimeout.current);
    }
    setShowChannelsDropdown(true);
  };

  const handleChannelsMouseLeave = () => {
    channelsHoverTimeout.current = setTimeout(() => {
      setShowChannelsDropdown(false);
    }, 300); // 300ms delay before closing
  };

  // All menu items displayed together (no split)
  const allMenuItems = menuItems;
  
  // Check if Channels or Leagues dropdown should show based on menu items
  const showChannelsMenu = menuItems.some(item => item.item_type === 'system' && item.href === '/channels' && item.has_dropdown);
  const showLeaguesMenu = menuItems.some(item => item.item_type === 'system' && item.href === '/leagues' && item.has_dropdown);

  const renderMenuItem = (link, index) => {
    // Handle system dropdown items (Leagues only now)
    if (link.item_type === 'system' && link.has_dropdown) {
      if (link.href === '/channels') {
        // Simple link - no dropdown
        return (
          <Link
            key={link.id || `menu-${index}`}
            to="/channels"
            className="flex items-center gap-1 font-heading text-sm font-bold uppercase tracking-wider text-white hover:text-orange-500 transition-colors"
            data-testid="channels-link"
          >
            <Radio className="w-4 h-4" />
            Channels
          </Link>
        );
      }
      
      if (link.href === '/leagues' && featuredLeagues.length > 0) {
        return (
          <div key={link.id || `menu-${index}`} className="relative" ref={leaguesDropdownRef}>
            <button
              onClick={() => setShowLeaguesDropdown(!showLeaguesDropdown)}
              className="flex items-center gap-1 font-heading text-sm font-bold uppercase tracking-wider text-white hover:text-orange-500 transition-colors"
              data-testid="leagues-dropdown-btn"
            >
              <Trophy className="w-4 h-4" />
              Leagues
              <ChevronDown className={`w-4 h-4 transition-transform ${showLeaguesDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showLeaguesDropdown && (
              <div className="absolute left-0 top-full mt-2 w-56 bg-zinc-900 border border-zinc-800 shadow-xl z-50">
                {featuredLeagues.map(league => (
                  <Link
                    key={league.id}
                    to={`/league/${league.slug}`}
                    onClick={() => setShowLeaguesDropdown(false)}
                    className="flex items-center gap-3 px-4 py-3 text-white hover:text-orange-500 hover:bg-zinc-800 transition-colors"
                  >
                    {league.logo_url ? (
                      <img src={league.logo_url} alt="" className="w-6 h-6 object-contain" />
                    ) : (
                      <div 
                        className="w-6 h-6 rounded flex items-center justify-center"
                        style={{ backgroundColor: league.color || '#f97316' }}
                      >
                        <Trophy className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <span className="text-sm font-medium">{league.abbreviation || league.name}</span>
                  </Link>
                ))}
                <Link
                  to="/leagues"
                  onClick={() => setShowLeaguesDropdown(false)}
                  className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-orange-500 hover:bg-zinc-800 transition-colors border-t border-zinc-800 text-xs uppercase tracking-wider"
                >
                  View All Leagues →
                </Link>
              </div>
            )}
          </div>
        );
      }
      
      // Return null if dropdown type but no data
      return null;
    }
    
    // Check if this is an event with logo display mode
    const showLogo = link.item_type === "event" && link.display_mode === "logo" && link.event_logo_url;
    
    const content = showLogo ? (
      <img 
        src={link.event_logo_url} 
        alt={link.name} 
        className="h-6 md:h-7 w-auto object-contain"
        title={link.name}
      />
    ) : (
      <span className="whitespace-nowrap">{link.name}</span>
    );

    if (link.is_external) {
      return (
        <a
          key={link.id || `menu-${index}`}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="font-heading text-sm font-bold uppercase tracking-wider text-white hover:text-orange-500 transition-colors"
        >
          {content}
        </a>
      );
    }
    
    return (
      <NavLink
        key={link.id || `menu-${index}`}
        to={link.href}
        className={({ isActive }) =>
          `font-heading text-sm font-bold uppercase tracking-wider transition-colors ${
            isActive ? "text-orange-500" : "text-white hover:text-orange-500"
          }`
        }
      >
        {content}
      </NavLink>
    );
  };

  return (
    <>
      {/* Sticky Navigation Menu - Logo LEFT, Menu RIGHT */}
      <header 
        data-testid="main-header"
        className="sticky top-0 z-50 bg-black border-b border-zinc-800 safe-area-top"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-[1400px] mx-auto px-4 safe-area-x">
          <div className="flex items-center h-16">
            
            {/* LEFT: ASN Logo */}
            <Link to="/" className="flex-shrink-0 mr-8">
              <img 
                src={ASN_LOGO} 
                alt="Antler Sports Network" 
                className="h-10 md:h-12 w-auto" 
              />
            </Link>

            {/* CENTER: Menu Items - Flex with consistent gap */}
            <nav className="hidden md:flex items-center gap-8 flex-1">
              {allMenuItems.map((item, index) => renderMenuItem(item, index))}
            </nav>

            {/* RIGHT: Notification Bell + Sign In / Profile - Always far right */}
            <div className="hidden md:flex items-center ml-8 gap-2">
              <NotificationBell variant="icon" />
              
              {currentUser ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    data-testid="user-account-button"
                    className="flex items-center gap-2 text-white hover:text-orange-500 transition-colors"
                  >
                    <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center border border-zinc-700 overflow-hidden">
                      {currentUser.profile_picture ? (
                        <img 
                          src={currentUser.profile_picture} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                    </div>
                  </button>
                  
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-1 w-80 bg-zinc-900 border border-zinc-800 shadow-xl z-50" data-testid="user-dropdown-menu">
                      <Link
                        to="/account"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-3 text-white hover:text-orange-500 hover:bg-zinc-800 transition-colors text-xs font-bold uppercase tracking-wider"
                      >
                        <User className="w-4 h-4" />
                        My Account
                      </Link>
                      <Link
                        to={currentUser?.is_plus ? "/plus/hub" : "/plus"}
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-xs font-bold uppercase tracking-wider border-t border-zinc-800"
                        data-testid="asn-plus-menu-link"
                      >
                        <img src="/asn-plus-logo.png" alt="ASN+" className="w-4 h-4 object-contain" />
                        <span className={currentUser?.is_plus ? 'text-orange-400' : 'text-white hover:text-orange-500'}>
                          {currentUser?.is_plus ? 'ASN Plus Hub' : 'Get ASN Plus'}
                        </span>
                      </Link>
                      
                      {/* My Teams Schedule */}
                      <div className="border-t border-zinc-800">
                        <div className="px-4 py-2 flex items-center justify-between">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">My Teams Schedule</span>
                          {myTeams.length > 0 && (
                            <span className="text-[10px] text-zinc-600">{myTeams.length} team{myTeams.length !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                        
                        {scheduleLoading ? (
                          <div className="px-4 py-4 text-center">
                            <div className="w-4 h-4 border-2 border-zinc-700 border-t-orange-500 rounded-full animate-spin mx-auto" />
                          </div>
                        ) : myTeams.length === 0 ? (
                          <div className="px-4 py-3 text-center">
                            <p className="text-zinc-600 text-xs">No teams followed yet</p>
                            <Link
                              to="/account"
                              onClick={() => setShowUserMenu(false)}
                              className="text-orange-500 text-xs hover:underline"
                            >
                              Follow teams →
                            </Link>
                          </div>
                        ) : myTeamsSchedule.length === 0 ? (
                          <div className="px-4 py-3 text-center">
                            <p className="text-zinc-600 text-xs">No upcoming games</p>
                          </div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto">
                            {myTeamsSchedule.map((game) => {
                              const home = game.home_team || {};
                              const away = game.away_team || {};
                              const dateStr = game.scheduled_date;
                              const timeStr = game.scheduled_time || '';
                              
                              // Format date
                              let displayDate = dateStr;
                              try {
                                const d = new Date(dateStr + 'T00:00:00');
                                displayDate = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                              } catch {}
                              
                              // Format time
                              let displayTime = timeStr;
                              try {
                                if (timeStr) {
                                  const [h, m] = timeStr.split(':');
                                  const hr = parseInt(h);
                                  displayTime = `${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
                                }
                              } catch {}
                              
                              return (
                                <Link
                                  key={game.id}
                                  to={game.channel_id ? `/watch/${game.channel_id}` : '/schedule'}
                                  onClick={() => setShowUserMenu(false)}
                                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 transition-colors border-t border-zinc-800/50"
                                  data-testid={`schedule-game-${game.id}`}
                                >
                                  {/* Teams */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      {away.logo_url && <img src={away.logo_url} alt="" className="w-4 h-4 object-contain" />}
                                      <span className="text-white text-xs font-medium truncate">{away.name || 'Away'}</span>
                                      <span className="text-zinc-600 text-[10px]">@</span>
                                      {home.logo_url && <img src={home.logo_url} alt="" className="w-4 h-4 object-contain" />}
                                      <span className="text-white text-xs font-medium truncate">{home.name || 'Home'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-zinc-500 text-[10px] flex items-center gap-1">
                                        <Calendar className="w-2.5 h-2.5" />
                                        {displayDate}
                                      </span>
                                      {displayTime && (
                                        <span className="text-zinc-500 text-[10px] flex items-center gap-1">
                                          <Clock className="w-2.5 h-2.5" />
                                          {displayTime}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {/* Live badge */}
                                  {game.is_live && (
                                    <span className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold uppercase animate-pulse">
                                      Live
                                    </span>
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                        
                        {myTeams.length > 0 && (
                          <Link
                            to="/account"
                            onClick={() => setShowUserMenu(false)}
                            className="block px-4 py-2 text-center text-[10px] text-zinc-500 hover:text-orange-500 border-t border-zinc-800 transition-colors"
                          >
                            Manage Followed Teams →
                          </Link>
                        )}
                      </div>
                      
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-3 text-white hover:text-orange-500 hover:bg-zinc-800 transition-colors text-xs font-bold uppercase tracking-wider border-t border-zinc-800"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login-user"
                  data-testid="sign-in-button"
                  className="px-5 py-2 bg-orange-500 text-black font-heading text-sm font-bold uppercase tracking-wider hover:bg-orange-400 transition-colors whitespace-nowrap"
                >
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile: Hamburger on right */}
            <div className="flex md:hidden items-center gap-3 ml-auto">
              {currentUser ? (
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="p-2 text-white relative"
                  data-testid="mobile-user-icon"
                >
                  <User className="w-5 h-5" />
                </button>
              ) : (
                <Link to="/login-user" className="p-2 text-orange-500">
                  <User className="w-5 h-5" />
                </Link>
              )}
              
              {/* Mobile user dropdown */}
              {showUserMenu && currentUser && (
                <div className="absolute right-2 top-14 w-72 bg-zinc-900 border border-zinc-800 shadow-xl z-[100]" data-testid="mobile-user-dropdown">
                  <Link
                    to="/account"
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 text-white hover:text-orange-500 hover:bg-zinc-800 transition-colors text-xs font-bold uppercase tracking-wider"
                  >
                    <User className="w-4 h-4" />
                    My Account
                  </Link>
                  
                  <div className="border-t border-zinc-800">
                    <div className="px-4 py-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">My Teams Schedule</span>
                    </div>
                    
                    {scheduleLoading ? (
                      <div className="px-4 py-4 text-center">
                        <div className="w-4 h-4 border-2 border-zinc-700 border-t-orange-500 rounded-full animate-spin mx-auto" />
                      </div>
                    ) : myTeams.length === 0 ? (
                      <div className="px-4 py-3 text-center">
                        <p className="text-zinc-600 text-xs">No teams followed yet</p>
                        <Link to="/account" onClick={() => setShowUserMenu(false)} className="text-orange-500 text-xs hover:underline">Follow teams →</Link>
                      </div>
                    ) : myTeamsSchedule.length === 0 ? (
                      <div className="px-4 py-3 text-center">
                        <p className="text-zinc-600 text-xs">No upcoming games</p>
                      </div>
                    ) : (
                      <div className="max-h-48 overflow-y-auto">
                        {myTeamsSchedule.slice(0, 5).map((game) => {
                          const home = game.home_team || {};
                          const away = game.away_team || {};
                          let displayDate = game.scheduled_date;
                          try { displayDate = new Date(game.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); } catch {}
                          return (
                            <Link
                              key={game.id}
                              to={game.channel_id ? `/watch/${game.channel_id}` : '/schedule'}
                              onClick={() => setShowUserMenu(false)}
                              className="flex items-center gap-2 px-4 py-2 hover:bg-zinc-800 transition-colors border-t border-zinc-800/50"
                              data-testid={`mobile-schedule-game-${game.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-white text-xs font-medium truncate">{away.name || 'Away'} @ {home.name || 'Home'}</div>
                                <div className="text-zinc-500 text-[10px] flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{displayDate}</div>
                              </div>
                              {game.is_live && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold animate-pulse">Live</span>}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-3 text-white hover:text-orange-500 hover:bg-zinc-800 transition-colors text-xs font-bold uppercase tracking-wider border-t border-zinc-800"
                  >
                    Sign Out
                  </button>
                </div>
              )}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-white hover:text-orange-500 transition-colors"
                data-testid="mobile-menu-button"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div 
            className="md:hidden border-t border-zinc-800 bg-black"
            style={{ 
              maxHeight: 'calc(100vh - 64px)',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            <nav className="py-4 px-2 space-y-1 overflow-hidden">
              {menuItems.map((link, index) => {
                const showLogo = link.item_type === "event" && link.display_mode === "logo" && link.event_logo_url;
                
                return link.is_external ? (
                  <a
                    key={link.id || `mobile-${index}`}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-3 font-heading text-sm font-bold uppercase tracking-wider text-white hover:text-orange-500 hover:bg-zinc-900 transition-colors truncate"
                  >
                    {showLogo && (
                      <img src={link.event_logo_url} alt="" className="w-5 h-5 object-contain shrink-0" />
                    )}
                    <span className="truncate">{link.name}</span>
                  </a>
                ) : (
                  <NavLink
                    key={link.id || `mobile-${index}`}
                    to={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-3 font-heading text-sm font-bold uppercase tracking-wider hover:bg-zinc-900 transition-colors truncate ${
                        isActive ? "text-orange-500" : "text-white hover:text-orange-500"
                      }`
                    }
                  >
                    {showLogo && (
                      <img src={link.event_logo_url} alt="" className="w-5 h-5 object-contain shrink-0" />
                    )}
                    <span className="truncate">{link.name}</span>
                  </NavLink>
                );
              })}
              
              {/* Channels Section in Mobile - Only show if added via Menu Manager */}
              {showChannelsMenu && channels.length > 0 && (
                <>
                  <div className="px-3 py-2 text-xs text-zinc-600 uppercase tracking-wider border-t border-zinc-800 mt-2 pt-2">Channels</div>
                  <div className="flex flex-wrap gap-2 px-3 py-2">
                    {channels.slice(0, 6).map((channel) => {
                      // Channels with a slug are conference networks - route to /network/{slug}
                      const isConferenceNetwork = Boolean(channel.slug);
                      const linkPath = isConferenceNetwork 
                        ? `/network/${channel.slug}` 
                        : `/watch/${channel.channel_id || channel.id}`;
                      
                      return (
                        <Link
                          key={channel.channel_id || channel.id}
                          to={linkPath}
                          onClick={() => setIsMenuOpen(false)}
                          className="w-12 h-12 bg-zinc-800 rounded-lg overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-orange-500 transition-all relative shrink-0"
                          title={channel.name}
                        >
                          {channel.logo_url ? (
                            <img src={channel.logo_url} alt={channel.name} className="w-9 h-9 object-contain" />
                          ) : (
                            <div 
                              className="w-full h-full flex items-center justify-center"
                              style={{ backgroundColor: channel.primary_color || '#374151' }}
                            >
                              <span className="text-white font-bold text-xs">{channel.name?.charAt(0) || 'C'}</span>
                            </div>
                          )}
                          {/* Live indicator */}
                          {channel.current_program?.is_live && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-zinc-800 animate-pulse" />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                  <Link
                    to="/channels"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-3 text-zinc-400 hover:text-orange-500 hover:bg-zinc-900 transition-colors text-xs"
                  >
                    View All Channels →
                  </Link>
                </>
              )}
              
              {/* Leagues Section in Mobile - Only show if added via Menu Manager */}
              {showLeaguesMenu && featuredLeagues.length > 0 && (
                <>
                  <div className="px-4 py-2 text-xs text-zinc-600 uppercase tracking-wider border-t border-zinc-800 mt-2 pt-2">Leagues</div>
                  {featuredLeagues.map((league) => (
                    <Link
                      key={league.id}
                      to={`/league/${league.slug}`}
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-white hover:text-orange-500 hover:bg-zinc-900 transition-colors"
                    >
                      {league.logo_url ? (
                        <img src={league.logo_url} alt="" className="w-6 h-6 object-contain" />
                      ) : (
                        <div 
                          className="w-6 h-6 rounded flex items-center justify-center"
                          style={{ backgroundColor: league.color || '#f97316' }}
                        >
                          <Trophy className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <span className="text-sm font-medium">{league.name}</span>
                    </Link>
                  ))}
                  <Link
                    to="/leagues"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-orange-500 hover:bg-zinc-900 transition-colors text-xs"
                  >
                    View All Leagues →
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
